import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Form,
  Badge,
} from "react-bootstrap";
import { useGetExpiringProductsQuery } from "../slices/productApiSlice";
import { useSelector } from "react-redux";

const VendorExpiryProductsScreen = () => {
  // Vendor ID from Redux (or however you store the logged-in user)
  const { userInfo } = useSelector((state) => state.auth);
  const vendorId = userInfo?._id;

  // Load expiry data
  const { data, isLoading, error } = useGetExpiringProductsQuery(vendorId);

  // Filters
  const [minDays, setMinDays] = useState("");
  const [maxDays, setMaxDays] = useState("");

  // Apply sorting + filtering
  const filteredAndSorted = useMemo(() => {
    if (!data) return [];

    let list = [...data];

    // Sort by daysLeft ASC
    list.sort((a, b) => a.daysLeft - b.daysLeft);

    // Range filtering
    return list.filter((p) => {
      const min = minDays !== "" ? Number(minDays) : null;
      const max = maxDays !== "" ? Number(maxDays) : null;

      if (min !== null && p.daysLeft < min) return false;
      if (max !== null && p.daysLeft > max) return false;

      return true;
    });
  }, [data, minDays, maxDays]);

  // Loading & Error Handling
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
        <Alert variant="danger">
          {error?.data?.message || "Failed to load expiry products"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Product Expiry Tracker</h2>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Minimum Days Left</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 0"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Maximum Days Left</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 30"
              value={maxDays}
              onChange={(e) => setMaxDays(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Expiring Products */}
      <Row>
        {filteredAndSorted.length > 0 ? (
          filteredAndSorted.map((item, index) => (
            <Col key={index} sm={12} md={6} lg={4} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <Card.Title>
                    {item.name}{" "}
                    {item.daysLeft <= 0 ? (
                      <Badge bg="danger">Expired</Badge>
                    ) : item.daysLeft <= 5 ? (
                      <Badge bg="warning" text="dark">
                        Expiring Soon
                      </Badge>
                    ) : (
                      <Badge bg="info">Fresh</Badge>
                    )}
                  </Card.Title>

                  <Card.Text>
                    <strong>Days Left:</strong>{" "}
                    {item.daysLeft <= 0 ? (
                      <span className="text-danger fw-bold">
                        {item.daysLeft} (Expired)
                      </span>
                    ) : (
                      <span className="text-primary fw-bold">
                        {item.daysLeft} Days
                      </span>
                    )}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col>
            <Alert variant="info">
              No products found for the selected filter range.
            </Alert>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default VendorExpiryProductsScreen;
